import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MonitoringMapComponent } from './components/monitoring-map/monitoring-map.component';
import { MapVehicle, STATUSES } from './cctv-monitoring-map.models';
import { Router } from '@angular/router';
import axiosJC261 from 'src/app/Service/axiosJC261';
import { GurdServiceService } from 'src/app/Service/gurd-service.service';
import { NotificationService } from 'src/app/Service/notification.service';
import { OtherService } from 'src/app/Service/other.service';
import { ImageRecord, MonitoringImage } from '../cctv-monitoring/cctv-monitoring.models';
import { normalizeImage } from '../cctv-monitoring/cctv-monitoring.utils';

@Component({
  selector: 'app-cctv-monitoring-map',
  templateUrl: './cctv-monitoring-map.component.html',
  styleUrls: ['./cctv-monitoring-map.component.css'],
})
export class CctvMonitoringMapComponent implements OnInit, OnDestroy {
  @ViewChild(MonitoringMapComponent) mapComponent?: MonitoringMapComponent;

  private destroyed = false;
  readonly statuses = STATUSES;
  vehicles: MapVehicle[] = [];
  filteredVehicles: MapVehicle[] = [];
  search = '';
  status = 'all';
  category: 'cabin' | 'outside' = 'cabin';
  onlyImages = false;
  showThumbnails = true;
  clustersEnabled = true;
  selectedVehicle: MapVehicle | null = null;
  visibleImageCount = 0;
  mapUnavailable = !(window as any).strMapUrl;
  images: MonitoringImage[] = [];
  hasLoaded = false;
  loading = false;
  loadError = '';

  constructor(
    private guard: GurdServiceService,
    private notification: NotificationService,
    private otherService: OtherService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadImages();
  }

  async loadImages(): Promise<void> {
    if (this.loading) return;
    this.loading = true;
    this.loadError = '';
    try {
      const response = await axiosJC261.post('GetLatestImages', {
        iSystemUser: Number(this.guard.decrypt(localStorage.getItem('ID'))),
        strSession: this.guard.decrypt(localStorage.getItem('Session')),
      });
      if (this.destroyed) return;
      const result = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
      if (result?.ISSessionExpired === true) {
        this.notification.showError('نشست کاربری شما منقضی شده است');
        this.otherService.removeLocalStorage();
        this.router.navigate(['login'], { queryParams: { error: '1xzOPwsZ' } });
        return;
      }
      if (result?.iResult === 0) throw new Error(result.strError || 'دریافت تصاویر ناموفق بود');
      const rows = Array.isArray(result) ? result :
        result?.Data || result?.data || result?.Table || result?.rows || result?.images;
      if (!Array.isArray(rows)) throw new Error('پاسخ دریافت تصاویر معتبر نیست');

      const baseUrl = new URL(axiosJC261.defaults.baseURL || '/', window.location.href).origin;
      const latest = new Map<string, MonitoringImage>();
      const vehicles = new Map<string, MapVehicle>();
      for (const row of rows as ImageRecord[]) {
        if (!row || typeof row !== 'object') continue;
        const id = String(row.strUnitID || row.strIMEI || '').trim();
        if (id) {
          const vehicle: MapVehicle = { ...row, strUnitID: id };
          const previous = vehicles.get(id);
          const valid = this.hasLocation(vehicle);
          const previousValid = previous && this.hasLocation(previous);
          if (!previous || (valid && !previousValid) ||
            (valid === !!previousValid && (Date.parse(vehicle.dtDateTime || '') || 0) >
              (Date.parse(previous.dtDateTime || '') || 0))) vehicles.set(id, vehicle);
        }
        const image = normalizeImage(row, baseUrl);
        if (!image) continue;
        const key = `${image.unitId}|${image.category}`;
        const previous = latest.get(key);
        if (!previous || (Date.parse(image.date) || 0) > (Date.parse(previous.date) || 0)) {
          latest.set(key, image);
        }
      }
      this.images = Array.from(latest.values());
      this.vehicles = Array.from(vehicles.values());
      this.hasLoaded = true;
      this.updateMarkers();
    } catch (error) {
      this.loadError = error instanceof Error && error.message !== 'Network Error'
        ? error.message : 'اتصال به سرور برقرار نشد';
      if (this.hasLoaded) this.notification.showError(this.loadError);
    } finally {
      this.loading = false;
    }
  }

  vehicleId(vehicle: MapVehicle): string {
    return String(vehicle.strUnitID || vehicle.strUnitId || '').trim();
  }

  imageFor(vehicle: MapVehicle): MonitoringImage | undefined {
    return this.images.find(image => image.unitId === this.vehicleId(vehicle) && image.category === this.category);
  }

  private hasLocation(vehicle: MapVehicle): boolean {
    if (vehicle.fLat == null || vehicle.fLon == null || vehicle.fLat === '' || vehicle.fLon === '') return false;
    const lat = Number(vehicle.fLat), lon = Number(vehicle.fLon);
    return Number.isFinite(lat) && Number.isFinite(lon) && lat >= -90 && lat <= 90
      && lon >= -180 && lon <= 180 && !(lat === 0 && lon === 0);
  }

  updateMarkers(): void {
    const query = this.search.trim().toLowerCase();
    this.filteredVehicles = this.vehicles.filter(vehicle => {
      return this.hasLocation(vehicle) && (this.status === 'all' || String(vehicle.tiDeviceStatus ?? 0) === this.status)
        && (!query || [vehicle.strFullName, vehicle.strVehicleNo, this.vehicleId(vehicle)]
          .some(value => String(value || '').toLowerCase().includes(query)))
        && (!this.onlyImages || !!this.imageFor(vehicle));
    });
    this.visibleImageCount = this.filteredVehicles.filter(vehicle => !!this.imageFor(vehicle)).length;
  }

  zoomToSelectedVehicle(): void {
    if (!this.selectedVehicle) return;
    this.mapComponent?.zoomToVehicle(this.selectedVehicle);
    this.selectedVehicle = null;
  }

  resetView(): void {
    this.mapComponent?.fitVehicles(this.filteredVehicles);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }
  
}
